import { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import config from '../../service/constants/appConfig';
import { companies } from '../../service/constants/companies';

const defaultTheme = (companies.find(company => company.isDefault) || companies[0]).theme;

const themesById = companies.reduce((themes, company) => {
  Object.values(company.ids).forEach(environmentIds => {
    [].concat(environmentIds).forEach(id => {
      themes[id.trim().toLowerCase()] = company.theme;
    });
  });
  return themes;
}, {});

export const getCompanyTheme = companyId => {
  const theme = themesById[companyId && companyId.toLowerCase()] || {};

  return {
    ...defaultTheme,
    ...theme,
    colors: { ...defaultTheme.colors, ...theme.colors },
  };
};

// Converte o tema nas variáveis CSS que o serviceProvider.scss lê.
export const themeCssVariables = theme =>
  Object.keys(theme.colors).reduce((variables, key) => {
    const name = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
    return { ...variables, [`--sp-${name}`]: theme.colors[key] };
  }, { '--sp-logo-scale': String(theme.logoScale) });

const CompanyContext = createContext({ companyId: config.serviceProviderCompanyId, theme: defaultTheme });

export const CompanyProvider = CompanyContext.Provider;

// Multitenant: a imobiliária vem da URL (/#/<companyId>/realEstate/...). A
// variável de ambiente só vale para os links antigos, sem o id no caminho.
//
// As cores vão no body porque o fundo da página é pintado nele; a tela herda
// as variáveis dali. useLayoutEffect para não piscar as cores padrão.
export function useCompanyFromRoute(match) {
  const companyId = (match && match.params && match.params.companyId) || config.serviceProviderCompanyId;
  const theme = useMemo(() => getCompanyTheme(companyId), [companyId]);

  useLayoutEffect(() => {
    const variables = themeCssVariables(theme);
    Object.keys(variables).forEach(name => document.body.style.setProperty(name, variables[name]));

    return () => {
      Object.keys(variables).forEach(name => document.body.style.removeProperty(name));
    };
  }, [theme]);

  return useMemo(() => ({ companyId, theme }), [companyId, theme]);
}

// Empresa da área realEstate atual ({ companyId, theme }), resolvida uma vez em
// realEstate/index.js para todas as telas de dentro.
export default function useCompany() {
  return useContext(CompanyContext);
}
